/* =========================================================================
 * Polo module — club gating.
 *
 * The Polo vertical (practices, players & handicaps, tournaments) is a gated
 * mirror of the Trips / Restaurant apps. It should only surface for clubs that
 * actually run polo.
 *
 * Gate source (in priority order):
 *   1. The backend `club.is_polo_club` flag, returned on getMe / login and
 *      persisted in the cached `user` payload (localStorage "user"). This is
 *      the source of truth once the club is flagged in the DB.
 *   2. An explicit club-id allowlist (PLYRS = club 139) — covers SUPER_ADMIN,
 *      whose active club is chosen via localStorage "Club_id" and whose full
 *      club object (with the flag) is not embedded in the cached `user`.
 *   3. An optional REACT_APP_POLO_ENABLED env flag so staging / demos / local
 *      dev can force the module on everywhere.
 * ========================================================================= */

/** Clubs explicitly opted into the Polo vertical. PLYRS is club id 139. */
const POLO_ENABLED_CLUBS: number[] = [139];

/** Force-enable Polo everywhere via env (staging, demos, local dev). */
const POLO_ENV_ENABLED =
  String(process.env.REACT_APP_POLO_ENABLED || "").toLowerCase() === "true";

function readCurrentUser(): any {
  try {
    return JSON.parse(localStorage.getItem("user") || "{}");
  } catch {
    return {};
  }
}

function readCurrentClubId(): number | null {
  try {
    const user = readCurrentUser();
    const selectedClubId = Number(localStorage.getItem("Club_id"));

    if (user?.role?.name === "SUPER_ADMIN" && Number.isInteger(selectedClubId)) {
      return selectedClubId;
    }

    const clubId = Number(user?.club_id ?? user?.club?.id);
    return Number.isInteger(clubId) ? clubId : null;
  } catch {
    return null;
  }
}

/** Read the backend `is_polo_club` flag from the cached login/club payload. */
function readIsPoloFlag(): boolean {
  try {
    const user = readCurrentUser();
    // The flag lives on the club object (`club.is_polo_club`); tolerate a
    // top-level copy too in case a future getMe shape hoists it.
    return Boolean(user?.club?.is_polo_club ?? user?.is_polo_club);
  } catch {
    return false;
  }
}

export function isPoloClub(): boolean {
  // 1. Backend-driven flag (source of truth once the club is flagged).
  if (readIsPoloFlag()) return true;
  // 2. env force-on for staging / demos / local dev.
  if (POLO_ENV_ENABLED) return true;
  // 3. Fallback allowlist (covers SUPER_ADMIN + not-yet-flagged clubs).
  const clubId = readCurrentClubId();
  return clubId != null && POLO_ENABLED_CLUBS.includes(clubId);
}

/**
 * Clubs that run Polo *instead of* the general platform: the app switcher shows
 * Polo only (no Operations / Trips / Restaurant) and every admin route outside
 * the Polo module bounces to /polo. PLYRS (139) is the first such club.
 */
const POLO_ONLY_CLUBS: number[] = [139];

function isSuperAdmin(): boolean {
  return readCurrentUser()?.role?.name === "SUPER_ADMIN";
}

export function isPoloOnlyClub(): boolean {
  // The polo-only lock scopes a club's own admins to their vertical. A
  // SUPER_ADMIN spans every club, so locking them into /polo while PLYRS is
  // their active club would strand them there — no club switcher, no way back
  // to the calendar. They keep the full chrome and only *see* Polo because
  // their selected club is a polo club.
  if (isSuperAdmin()) return false;
  if (!isPoloClub()) return false;
  const clubId = readCurrentClubId();
  return clubId != null && POLO_ONLY_CLUBS.includes(clubId);
}

/**
 * Routes a Polo-only club may reach. Everything the admin needs lives under
 * /polo — including Support, mounted at /polo/support inside the Polo chrome
 * (the shared /support page hangs off the Operations sidebar these clubs no
 * longer have).
 */
const POLO_ONLY_ALLOWED_PATHS = ["/polo", "/signin", "/partner"];

export function isPathAllowedForPoloOnly(pathname: string): boolean {
  return POLO_ONLY_ALLOWED_PATHS.some(
    (p) => pathname === p || pathname.startsWith(p + "/")
  );
}
